import { EventEmitter2 } from 'eventemitter2';

import nowTimestamp from '../../utils/dates/nowTimestamp';
import delay from '../../utils/delay';
import { isMobile } from '../../utils/environment';
import makeLogger, { type ReadwiseLogger } from '../../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import { KeysOfType } from '../../utils/typescriptUtils';
import type { CacheInstance } from '../Cache';
import { Cache } from '../cache.platform';
// eslint-disable-next-line import/no-cycle
import { ItemFetcher } from './ItemFetcher';
import type { ItemStoreById, ItemStoreConstructorArgs } from './types';


type OngoingFullLoadAll = {
  nextCursor: string;
  requestStartDatetime: number;
};

class CacheBackedStore<TItem, TIdPropertyName extends KeysOfType<TItem, string>>
  extends EventEmitter2 implements ItemStoreById<TItem> {
  hasLoadedAll: boolean;
  hasLoadedAny: boolean;
  readonly name: string;

  protected _cache: CacheInstance;
  protected readonly _idPropertyName: TIdPropertyName;
  protected readonly _itemFetcher: ItemFetcher<TItem, TIdPropertyName>;
  private _logger: ReadwiseLogger;
  private _ongoingLoadAllPromise: Promise<void> | null = null;
  private _loadAllAbortController: AbortController | null = null;

  constructor(options: ItemStoreConstructorArgs) {
    super();
    this.hasLoadedAll = false;
    this.hasLoadedAny = false;

    this._logger = makeLogger(`${options.name} store`, { shouldLogToNewRelic: true, shouldLog: true });

    this.name = options.name;

    this._itemFetcher = new ItemFetcher(options);

    this._idPropertyName = (options.idPropertyName || 'id') as unknown as TIdPropertyName;

    // Use a separate namespace / DB for this store's cache (this does not create a DB every time its called)
    this._cache = Cache.createInstance({
      name: `store__${options.name}`,
    });
  }

  async clearCache(): Promise<void> {
    this._logger.debug('Clearing cache...');
    await this._cache.clear();
    this._logger.debug('Cleared cache!');
  }

  async getCachedItemIds(): Promise<string[]> {
    const allKeys = await this._cache.keys();

    return allKeys
      .filter((key) => key.startsWith('item__'))
      .map((key) => key.replace('item__', ''));
  }

  async getItemsByIds(ids: string[]): Promise<{ [id: string]: TItem; }> {
    const keys = ids.map((id) => this.makeItemCacheKey(id));
    const itemsByKey = await this._cache.getItems<TItem>(keys);
    const entries = Object.entries(itemsByKey).map(([key, item]) => [key.replace(/^item__/, ''), item as TItem]);
    return Object.fromEntries(entries);
  }

  async removeByIds(ids: string[]): Promise<void> {
    if (ids.length === 0) {
      return;
    }
    const keys = ids.map((id) => this.makeItemCacheKey(id));
    await this._cache.removeItems(keys);

    const items = ids.map((id) => ({ id }));
    this.emit('items-removed', { items });
  }

  async loadByIds(
    idsToLoad: string[],
    shouldBypassCache?: boolean,
    extraQueryParams?: URLSearchParams,
  ): Promise<{ [id: string]: TItem; }> {
    this._logger.debug('loadByIds', { idsToLoad, shouldBypassCache });
    if (idsToLoad.length === 0) {
      throw new Error('No IDs given');
    }

    try {
      this._logger.debug('loadByIds', { idsToLoad });
      // Skip reloading cached items. Exit if all are cached
      let idsToLoadFromServer = idsToLoad;

      let itemsFromCache: TItem[] = [];

      if (!shouldBypassCache) {
        const cacheKeys = idsToLoad.map((id) => this.makeItemCacheKey(id));
        const itemsFromCacheByKey = await this._cache.getItems(cacheKeys) as { [key: string]: TItem; };

        itemsFromCache = Object.values(itemsFromCacheByKey);

        if (itemsFromCache.length > 0) {
          this.emit('items-received-by-id', { items: itemsFromCache, missingIds: [] });
        }

        idsToLoadFromServer = cacheKeys
          .filter((id) => !(id in itemsFromCacheByKey))
          .map((id) => id.replace('item__', '')) as unknown as string[];
      }
      const itemMap = Object.fromEntries(
        itemsFromCache.map((item) => [item[this._idPropertyName] as string, item]),
      );

      if (idsToLoadFromServer.length > 0) {
        await this._itemFetcher.fetchItemsById({
          idsToFetch: idsToLoadFromServer,
          onItemPageReceived: async (items, requestedIds) => {
            await this.upsertIntoCache(requestedIds, items);
            for (const item of items) {
              itemMap[item[this._idPropertyName] as string] = item;
            }
          },
          extraQueryParams,
        });
      }
      this._logger.debug('loadByIds returning', {
        itemsFromCache: itemsFromCache.length,
        idsToLoadFromServer: idsToLoadFromServer.length,
        items: Object.keys(itemMap),
      });
      return itemMap;
    } catch (e) {
      this._logger.error('Error while loading by IDs', {
        error: e,
        errorMessage: e instanceof Error ? e.message : String(e),
        errorStack: e instanceof Error ? e.stack : undefined,
        idsToLoad,
      });
      if (e instanceof Error) {
        throw e;
      } else {
        throw new Error(`unknown error: ${e}`);
      }
    }
  }

  async loadAll() {
    this._logger.debug('loadAll called');
    // Re-use the load-all promise as it can trigged after a timeout or based on user behaviour
    if (this._ongoingLoadAllPromise) {
      return this._ongoingLoadAllPromise;
    }
    this._logger.debug('loadAll actually starting');

    this._loadAllAbortController = new AbortController();
    const signal = this._loadAllAbortController.signal;

    /*
      If we're loading all *again*:

      1. First we check if there was an ongoing full load all last time the app was
        running; i.e. the load all we do when the first app is opened for the first
        time / after the cache is cleared. If so, we finish that first (continuing
        from the next page cursor).
      2. Then / otherwise, we only load those that have changed since the last time
        (request start time).
    */
    let updatedSince: number | undefined;
    let cursorToStartAt: string | undefined;
    const ongoingFullLoadAll = await this._cache.getItem<OngoingFullLoadAll>('ongoingFullLoadAll');

    if (ongoingFullLoadAll) {
      cursorToStartAt = ongoingFullLoadAll.nextCursor;
    } else {
      // If there's no ongoing load all, get all updates since last time we synced:
      const lastLoadAllDatetime = await this._cache.getItem<number>('lastLoadAllDatetime');
      if (lastLoadAllDatetime) {
        updatedSince = lastLoadAllDatetime;

        const ongoingLoadUpdatesCursor = await this._cache.getItem<string>('ongoingLoadUpdatesCursor');
        if (ongoingLoadUpdatesCursor) {
          cursorToStartAt = ongoingLoadUpdatesCursor;
        }
      }
    }

    const thisRequestStartDatetime = nowTimestamp();
    const loadPromise = this._itemFetcher.fetchItemsByCursor({
      updatedSince,
      cursorToStartAt,
      signal,
      onItemPageReceived: async (items, requestedIds, nextCursor) => {
        if (signal.aborted) {
          this._logger.debug('loadAll aborted, skipping page processing');
          return;
        }
        await this.upsertIntoCache(requestedIds, items, signal);

        this.hasLoadedAny = true;
        this.emitLoadedStatusChangedEvent();

        if (ongoingFullLoadAll) {
          // Cache the next cursor in case the app is closed, so we can continue next time it's opened.
          if (nextCursor) {
            await this._cache.setItem<OngoingFullLoadAll>('ongoingFullLoadAll', {
              nextCursor,
              requestStartDatetime: thisRequestStartDatetime,
            });
          } else {
            // Completed first full load, so we can remove it from cache and only load updates from now on.
            await this._cache.removeItem('ongoingFullLoadAll');
          }
        } else if (updatedSince) {
          // When loading updates, we can still have many pages of updates.
          // Cache the cursor, so if the app is closed we can continue non-wastefully.
          if (nextCursor) {
            await this._cache.setItem('ongoingLoadUpdatesCursor', nextCursor);
          } else {
            await this._cache.removeItem('ongoingLoadUpdatesCursor');
          }
        }
      },
    });

    // When loading all, we need to do some stuff once done
    this._ongoingLoadAllPromise = loadPromise
      .then(() => {
        // If we've successfully loaded all:
        this.hasLoadedAll = true;
        this.emitLoadedStatusChangedEvent();
        this._ongoingLoadAllPromise = null;
        this._loadAllAbortController = null;
        // No need to wait for this promise:
        const lastLoadAllDatetime = ongoingFullLoadAll?.requestStartDatetime ?? thisRequestStartDatetime;
        this._cache.setItem('lastLoadAllDatetime', lastLoadAllDatetime);
      })
      .catch((e) => {
        this._ongoingLoadAllPromise = null;
        this._loadAllAbortController = null;
        throw e;
      });

    return loadPromise;
  }

  async abortLoadAll(): Promise<void> {
    if (!this._loadAllAbortController) {
      return;
    }

    this._logger.debug('Aborting loadAll operation');
    this._loadAllAbortController.abort();

    if (this._ongoingLoadAllPromise) {
      try {
        await this._ongoingLoadAllPromise;
      } catch (e) {
        if (e instanceof Error && e.name !== 'AbortError') {
          throw e;
        }
      }
    }
    this._logger.debug('loadAll operation aborted');
  }


  protected async upsertIntoCache(requestedIds: string[], items: TItem[], signal?: AbortSignal): Promise<void> {
    const startTime = performance.now();
    const itemsActuallyInserted: TItem[] = [];

    for (const item of items) {
      // Check abort signal before each cache operation
      if (signal?.aborted) {
        this._logger.debug('upsertIntoCache aborted during processing', {
          processedItems: items.indexOf(item),
          totalItems: items.length,
          itemsInserted: itemsActuallyInserted.length,
        });
        return;
      }

      await this._cache.setItem(this.makeItemCacheKey(item[this._idPropertyName] as string), item);
      itemsActuallyInserted.push(item);
      await delay(isMobile ? 5 : 1);
    }
    // NOTE: There is no code concerned with checking if an item already exists or anything like that
    //  Right now that needs to be handled in the foreground (e.g see fetchRelatedRSSUnthrottled)
    if (itemsActuallyInserted.length > 0 && !signal?.aborted) {
      this.emit('items-received-from-server', { items: itemsActuallyInserted });
    }

    if (requestedIds.length > 0) {
      const identifiersFound = new Set(items.map((item) => item[this._idPropertyName] as string));
      const missingIds = requestedIds.filter((id) => !identifiersFound.has(id));
      if (missingIds.length > 0) {
        this._logger.warn('upsertIntoCache missing Ids ', {
          missingIds,
        });
      }
      this.emit('items-received-by-id', { items, missingIds });
    }

    const duration = (performance.now() - startTime).toFixed(3);
    this._logger.debug(`upsertIntoCache took ${duration}ms`, {
      itemCount: items.length,
      itemsActuallyInserted: itemsActuallyInserted.length,
      wasAborted: signal?.aborted,
    });
  }


  private makeItemCacheKey(id: string): string {
    return `item__${id}`;
  }

  private emitLoadedStatusChangedEvent() {
    this.emit('loaded-state-changed', {
      hasLoadedAll: this.hasLoadedAll,
      hasLoadedAny: this.hasLoadedAny,
    });
  }
}

export default CacheBackedStore;
